import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DollarSign, UserPlus, CheckCircle2 } from 'lucide-react';

export const StaffTab = ({
  pendingPayments, pendingTotalOwed, staffSummary, markingPaidId,
  markStaffPaymentPaid, fetchPendingPayments,
  staffForm, setStaffForm, addStaffMember, staff,
}) => (
  <div className="space-y-8" data-testid="staff-tab">
    {/* Pending Staff Payouts */}
    <Card className="card-tech p-6" data-testid="pending-payments-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h3 className="text-xl font-bold text-green-400">
            <DollarSign className="w-5 h-5 inline mr-2" /> Pending Staff Payouts
          </h3>
          <p className="text-green-500/70 text-sm">
            Total owed: <span className="text-yellow-400 font-bold">${pendingTotalOwed.toFixed(2)}</span>
            {' '}across {pendingPayments.length} payment(s). Send funds via PayPal, then mark them paid here.
          </p>
        </div>
        <Button onClick={fetchPendingPayments} className="btn-secondary text-xs" data-testid="refresh-payouts-btn">
          Refresh
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-green-500/20">
              <th className="text-left text-green-400 py-3 px-2">Staff</th>
              <th className="text-left text-green-400 py-3 px-2">PayPal Email</th>
              <th className="text-left text-green-400 py-3 px-2">Period</th>
              <th className="text-right text-green-400 py-3 px-2">Amount</th>
              <th className="text-right text-green-400 py-3 px-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingPayments.map((p) => (
              <tr key={p.id} className="border-b border-green-500/10" data-testid={`pending-payment-${p.id}`}>
                <td className="py-3 px-2 text-green-300 text-sm">{p.staff_name || p.staff_id}</td>
                <td className="py-3 px-2 text-green-300 text-xs font-mono">{p.staff_paypal_email || '—'}</td>
                <td className="py-3 px-2 text-green-300 text-xs">
                  {(p.period_start || '').slice(0, 10)} → {(p.period_end || '').slice(0, 10)}
                </td>
                <td className="py-3 px-2 text-right text-yellow-400 font-mono">${(p.amount || 0).toFixed(2)}</td>
                <td className="py-3 px-2 text-right">
                  <Button onClick={() => markStaffPaymentPaid(p.id)} disabled={markingPaidId === p.id}
                    className="btn-primary text-xs py-1" data-testid={`mark-paid-btn-${p.id}`}>
                    {markingPaidId === p.id ? '...' : <><CheckCircle2 className="w-3 h-3 inline mr-1" />Mark Paid</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pendingPayments.length === 0 && (
          <p className="text-center text-green-500/50 py-6">No pending payouts. Calculate a period below to generate payments.</p>
        )}
      </div>
    </Card>

    {/* YTD Staff Summary */}
    <Card className="card-tech p-6" data-testid="staff-summary-card">
      <h3 className="text-xl font-bold text-green-400 mb-4">YTD Staff Compensation ({new Date().getFullYear()})</h3>
      <p className="text-green-500/70 text-sm mb-4">
        Staff paid <span className="text-yellow-400">$600+</span> in a calendar year require IRS Form 1099-NEC.
      </p>
      {staffSummary.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-green-500/20">
                <th className="text-left text-green-400 py-3 px-2">Name</th>
                <th className="text-left text-green-400 py-3 px-2">Role</th>
                <th className="text-right text-green-400 py-3 px-2">Paid YTD</th>
                <th className="text-right text-green-400 py-3 px-2">Pending</th>
                <th className="text-right text-green-400 py-3 px-2">1099-NEC?</th>
              </tr>
            </thead>
            <tbody>
              {staffSummary.map((s) => (
                <tr key={s.staff_id} className="border-b border-green-500/10">
                  <td className="py-3 px-2 text-green-300 text-sm">{s.name}</td>
                  <td className="py-3 px-2 text-green-300 text-sm">{s.role}</td>
                  <td className="py-3 px-2 text-right text-green-300 font-mono">${s.paid_ytd.toFixed(2)}</td>
                  <td className="py-3 px-2 text-right text-yellow-400 font-mono">${s.pending_ytd.toFixed(2)}</td>
                  <td className="py-3 px-2 text-right">
                    {s.requires_1099_nec ? (
                      <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">REQUIRED</span>
                    ) : (
                      <span className="text-green-500/50 text-xs">no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-6 bg-green-500/5 border border-green-500/10 rounded" data-testid="staff-summary-empty">
          <p className="text-green-400 font-bold text-lg">$0 YTD · 0 staff</p>
          <p className="text-green-500/60 text-sm mt-1">
            No staff below the 1099-NEC threshold — no forms required yet. Add staff below to start tracking.
          </p>
        </div>
      )}
    </Card>

    <Card className="card-tech p-6">
      <h3 className="text-xl font-bold text-green-400 mb-4"><UserPlus className="w-5 h-5 inline mr-2" /> Add Staff Member</h3>
      <p className="text-green-300 text-sm mb-4">Staff receive legitimate compensation for services rendered. Revenue sharing is a legal business expense.</p>
      <form onSubmit={addStaffMember} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input value={staffForm.name} onChange={e => setStaffForm({...staffForm, name: e.target.value})} placeholder="Full Name" className="bg-black/50 border-green-500/30 text-green-300" required data-testid="staff-name" />
        <Input value={staffForm.email} onChange={e => setStaffForm({...staffForm, email: e.target.value})} placeholder="Email" type="email" className="bg-black/50 border-green-500/30 text-green-300" required data-testid="staff-email" />
        <Input value={staffForm.role} onChange={e => setStaffForm({...staffForm, role: e.target.value})} placeholder="Role (e.g. Marketing)" className="bg-black/50 border-green-500/30 text-green-300" required data-testid="staff-role" />
        <Input value={staffForm.revenue_share_percent} onChange={e => setStaffForm({...staffForm, revenue_share_percent: parseFloat(e.target.value) || 0})} placeholder="Revenue Share %" type="number" step="0.1" className="bg-black/50 border-green-500/30 text-green-300" required data-testid="staff-share" />
        <Input value={staffForm.paypal_email} onChange={e => setStaffForm({...staffForm, paypal_email: e.target.value})} placeholder="PayPal Email" type="email" className="bg-black/50 border-green-500/30 text-green-300" required data-testid="staff-paypal" />
        <Button type="submit" className="btn-primary" data-testid="add-staff-button">Add Staff Member</Button>
      </form>
    </Card>

    <Card className="card-tech p-6">
      <h3 className="text-xl font-bold text-green-400 mb-4">Current Staff ({staff.length})</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-green-500/20">
              <th className="text-left text-green-400 py-3 px-2">Name</th>
              <th className="text-left text-green-400 py-3 px-2">Role</th>
              <th className="text-left text-green-400 py-3 px-2">Email</th>
              <th className="text-right text-green-400 py-3 px-2">Revenue Share</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s, idx) => (
              <tr key={s.id || s.email || idx} className="border-b border-green-500/10">
                <td className="py-3 px-2 text-green-300">{s.name}</td>
                <td className="py-3 px-2 text-green-300">{s.role}</td>
                <td className="py-3 px-2 text-green-300 text-sm">{s.email}</td>
                <td className="py-3 px-2 text-right text-green-500 font-bold">{s.revenue_share_percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        {staff.length === 0 && <p className="text-center text-green-500/50 py-8">No staff members yet.</p>}
      </div>
    </Card>
  </div>
);
